/**
 * RS_FloorCalculator — Smart Coverage Calculator for flooring SKUs v1
 *
 * 3-step guided purchase flow:
 *   1. Room Area (single input, imperial/metric)
 *   2. Waste Factor (0/5/10/15%)
 *   3. Overview + Add to Cart
 *
 * Each Shopify "unit" is one box. sqft per box comes from the product
 * metafield custom.sqft_coverage (passed in via the rendered Liquid snippet).
 *
 * Mirrors the UX patterns of rs-panel-calculator (progress bar, completed-step
 * previews, AJAX add-to-cart with cart-drawer rerender) but without variant
 * parsing, size recommendations, pack optimisation or style selection — none
 * of which apply to single-variant flooring products.
 */

if (!customElements.get('rs-floor-calculator')) {
  class RS_FloorCalculator extends HTMLElement {
    constructor() {
      super();
      this.currentStep = 1;
      this.units = localStorage.getItem('rs-calc-units') || 'imperial';
      this.roomAreaSqFt = 0;
      this.wasteFactor = 0.10;
      this.productData = null;
      this.sqftPerBox = 0;
      this.boxesNeeded = 0;
      this.totalCoverageSqFt = 0;
      this.overageSqFt = 0;
    }

    connectedCallback() {
      this.productData = this._parseProductJSON();
      if (!this.productData) return;

      this.sqftPerBox = Number(this.productData.sqftPerBox) || 0;
      if (this.sqftPerBox <= 0) {
        this._showError('Coverage per box is not configured for this product. Please contact us before ordering.');
        return;
      }

      this._bindEvents();
      this._initUnitToggle();
      this._renderStep(1);
    }

    /* ── Product Data Parsing ─────────────────────────────────────────── */

    _parseProductJSON() {
      const script = this.querySelector('[data-rs-calc-product]');
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        console.error('RS_FloorCalculator: Failed to parse product JSON', e);
        return null;
      }
    }

    _getSelectedVariant() {
      const variants = this.productData?.variants || [];
      const selectedId = this.productData?.selectedVariantId;
      const byId = variants.find((v) => Number(v.id) === Number(selectedId));
      if (byId && byId.available !== false) return byId;
      return variants.find((v) => v.available !== false) || variants[0] || null;
    }

    /* ── Event Binding ────────────────────────────────────────────────── */

    _bindEvents() {
      this.addEventListener('click', (e) => {
        const target = e.target.closest('[data-rs-calc-action]');
        if (!target) return;

        switch (target.dataset.rsCalcAction) {
          case 'toggle-unit': this._handleUnitToggle(target); break;
          case 'select-waste': this._handleWasteSelect(target); break;
          case 'continue': this._advanceStep(); break;
          case 'edit-step': this._goToStep(parseInt(target.dataset.rsCalcStep, 10)); break;
          case 'add-to-cart': this._addToCart(); break;
        }
      });

      this.addEventListener('input', (e) => {
        if (e.target.matches('[data-rs-calc-input="area"]')) {
          this._handleAreaInput();
        }
      });
    }

    /* ── Unit Toggle ──────────────────────────────────────────────────── */

    _initUnitToggle() {
      this.querySelectorAll('[data-rs-calc-action="toggle-unit"]').forEach((tab) => {
        tab.classList.toggle('rs-floor-calc__toggle-tab--active', tab.dataset.rsCalcUnit === this.units);
        const input = tab.querySelector('input[type="radio"]');
        if (input) input.checked = tab.dataset.rsCalcUnit === this.units;
      });
      this._updateInputLabels();
    }

    _handleUnitToggle(target) {
      const unit = target.dataset.rsCalcUnit;
      if (unit === this.units) return;

      this.units = unit;
      localStorage.setItem('rs-calc-units', unit);

      this.querySelectorAll('[data-rs-calc-action="toggle-unit"]').forEach((t) => {
        t.classList.toggle('rs-floor-calc__toggle-tab--active', t.dataset.rsCalcUnit === unit);
        const input = t.querySelector('input[type="radio"]');
        if (input) input.checked = t.dataset.rsCalcUnit === unit;
      });

      this._convertInputValue();
      this._updateInputLabels();
    }

    _updateInputLabels() {
      const label = this.querySelector('[data-rs-calc-label="area"]');
      if (label) label.textContent = this.units === 'imperial' ? 'Room Area (sq ft)' : 'Room Area (m²)';
    }

    _convertInputValue() {
      const input = this.querySelector('[data-rs-calc-input="area"]');
      if (!input) return;
      const val = parseFloat(input.value);
      if (!val) return;

      if (this.units === 'metric') {
        // Was sq ft → display m². 1 m² = 10.7639 sq ft.
        input.value = (val / 10.7639).toFixed(2);
      } else {
        // Was m² → display sq ft.
        input.value = (val * 10.7639).toFixed(2);
      }
    }

    /* ── Area Input ───────────────────────────────────────────────────── */

    _handleAreaInput() {
      const input = this.querySelector('[data-rs-calc-input="area"]');
      let area = parseFloat(input?.value) || 0;
      // Always store in sq ft internally.
      if (this.units === 'metric') area = area * 10.7639;
      this.roomAreaSqFt = area;
    }

    /* ── Waste Selection ──────────────────────────────────────────────── */

    _handleWasteSelect(target) {
      const waste = parseFloat(target.dataset.rsCalcWaste);
      this.wasteFactor = waste;

      this.querySelectorAll('[data-rs-calc-action="select-waste"]').forEach((chip) => {
        chip.classList.toggle('rs-floor-calc__waste-chip--selected', parseFloat(chip.dataset.rsCalcWaste) === waste);
        const input = chip.querySelector('input[type="radio"]');
        if (input) input.checked = parseFloat(chip.dataset.rsCalcWaste) === waste;
      });

      const disclaimer = this.querySelector('[data-rs-calc-disclaimer]');
      if (disclaimer) {
        disclaimer.classList.toggle('rs-floor-calc__disclaimer--visible', waste === 0);
      }
    }

    /* ── Step Machine ─────────────────────────────────────────────────── */

    _advanceStep() {
      if (this.currentStep === 1) {
        this._handleAreaInput();
        if (this.roomAreaSqFt <= 0) {
          this._showError('Please enter a room area greater than zero.');
          return;
        }
      }
      if (this.currentStep < 3) {
        this._goToStep(this.currentStep + 1);
      }
    }

    _goToStep(step) {
      this.currentStep = step;
      this._renderStep(step);
    }

    _renderStep(activeStep) {
      for (let i = 1; i <= 3; i++) {
        const stepEl = this.querySelector(`[data-rs-calc-step="${i}"]`);
        if (!stepEl) continue;

        const preview = stepEl.querySelector('[data-rs-calc-step-preview]');
        const content = stepEl.querySelector('[data-rs-calc-step-content]');
        const pending = stepEl.querySelector('[data-rs-calc-step-pending]');

        if (i < activeStep) {
          /* Visited / completed — summary row only */
          stepEl.hidden = false;
          if (preview) preview.style.display = 'flex';
          if (content) content.style.display = 'none';
          if (pending) pending.style.display = 'none';
          this._updateStepPreview(i);
        } else if (i === activeStep) {
          /* Active step — full content */
          stepEl.hidden = false;
          if (preview) preview.style.display = 'none';
          if (content) content.style.display = 'flex';
          if (pending) pending.style.display = 'none';
          if (i === 3) {
            this._calculateBoxes();
            this._renderOverview();
            this._updatePriceBreakdown();
            this._updateATCButton();
          }
        } else {
          /* Not active and not visited — hide entire step */
          stepEl.hidden = true;
          if (preview) preview.style.display = 'none';
          if (content) content.style.display = 'none';
          if (pending) pending.style.display = 'none';
        }
      }

      this._updateProgressBar(activeStep);
    }

    _updateProgressBar(activeStep) {
      this.querySelectorAll('[data-rs-calc-tab]').forEach((tab) => {
        const tabStep = parseInt(tab.dataset.rsCalcTab, 10);
        tab.classList.remove(
          'rs-floor-calc__progress-tab--completed',
          'rs-floor-calc__progress-tab--active',
          'rs-floor-calc__progress-tab--pending'
        );

        if (tabStep < activeStep) {
          tab.classList.add('rs-floor-calc__progress-tab--completed');
          if (!tab.querySelector('.check-icon')) {
            tab.insertAdjacentHTML(
              'afterbegin',
              '<svg class="check-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><polyline points="20 6 9 17 4 12"/></svg>'
            );
          }
        } else if (tabStep === activeStep) {
          tab.classList.add('rs-floor-calc__progress-tab--active');
          tab.querySelector('.check-icon')?.remove();
        } else {
          tab.classList.add('rs-floor-calc__progress-tab--pending');
          tab.querySelector('.check-icon')?.remove();
        }
      });
    }

    _updateStepPreview(step) {
      const valueEl = this.querySelector(`[data-rs-calc-step="${step}"] [data-rs-calc-preview-value]`);
      if (!valueEl) return;

      switch (step) {
        case 1: {
          if (this.units === 'imperial') {
            valueEl.textContent = `${this.roomAreaSqFt.toFixed(0)} sq ft`;
          } else {
            valueEl.textContent = `${(this.roomAreaSqFt / 10.7639).toFixed(1)} m²`;
          }
          break;
        }
        case 2:
          valueEl.textContent = `${(this.wasteFactor * 100).toFixed(0)}% waste allowance`;
          break;
      }
    }

    /* ── Box Calculation ──────────────────────────────────────────────── */

    _calculateBoxes() {
      const area = this.roomAreaSqFt;
      const perBox = this.sqftPerBox;
      if (area <= 0 || perBox <= 0) {
        this.boxesNeeded = 0;
        this.totalCoverageSqFt = 0;
        this.overageSqFt = 0;
        return;
      }
      const requiredSqFt = area * (1 + this.wasteFactor);
      this.boxesNeeded = Math.ceil(requiredSqFt / perBox);
      this.totalCoverageSqFt = this.boxesNeeded * perBox;
      this.overageSqFt = this.totalCoverageSqFt - area;
    }

    /* ── Overview Rendering ───────────────────────────────────────────── */

    _renderOverview() {
      const container = this.querySelector('[data-rs-calc-breakdown]');
      if (!container) return;

      const wasteSqFt = this.roomAreaSqFt * this.wasteFactor;
      const areaLabel = this.units === 'imperial'
        ? `${this.roomAreaSqFt.toFixed(0)} sq ft`
        : `${(this.roomAreaSqFt / 10.7639).toFixed(1)} m²`;

      container.innerHTML = `
        <div class="rs-floor-calc__row">
          <span class="rs-floor-calc__row-label">Room coverage</span>
          <span class="rs-floor-calc__row-value">${areaLabel}</span>
        </div>
        <div class="rs-floor-calc__row">
          <span class="rs-floor-calc__row-label">Waste allowance (${(this.wasteFactor * 100).toFixed(0)}%)</span>
          <span class="rs-floor-calc__row-value">+${wasteSqFt.toFixed(1)} sq ft</span>
        </div>
        <div class="rs-floor-calc__row">
          <span class="rs-floor-calc__row-label">Coverage per box</span>
          <span class="rs-floor-calc__row-value">${this.sqftPerBox.toFixed(2)} sq ft</span>
        </div>
        <div class="rs-floor-calc__divider"></div>
        <div class="rs-floor-calc__row rs-floor-calc__row--highlight">
          <span class="rs-floor-calc__total-label">Boxes required</span>
          <span class="rs-floor-calc__total-value">× ${this.boxesNeeded}</span>
        </div>
        <div class="rs-floor-calc__row">
          <span class="rs-floor-calc__row-label">Total coverage</span>
          <span class="rs-floor-calc__row-value">${this.totalCoverageSqFt.toFixed(1)} sq ft</span>
        </div>
        <div class="rs-floor-calc__row">
          <span class="rs-floor-calc__row-label">Leftover material</span>
          <span class="rs-floor-calc__row-value">${this.overageSqFt.toFixed(1)} sq ft</span>
        </div>
      `;
    }

    _updatePriceBreakdown() {
      const container = this.querySelector('[data-rs-calc-price-breakdown]');
      if (!container) return;

      const variant = this._getSelectedVariant();
      if (!variant || this.boxesNeeded <= 0) {
        container.innerHTML = '';
        container.style.display = 'none';
        return;
      }

      const unitPrice = variant.price / 100;
      const totalPrice = unitPrice * this.boxesNeeded;

      container.innerHTML = `
        <div class="rs-floor-calc__price-row">
          <span class="rs-floor-calc__price-row-label">Box × ${this.boxesNeeded}</span>
          <span class="rs-floor-calc__price-row-value">${this._formatMoney(unitPrice)} each</span>
        </div>
        <div class="rs-floor-calc__price-divider"></div>
        <div class="rs-floor-calc__price-total">
          <span>Total</span>
          <span>${this._formatMoney(totalPrice)}</span>
        </div>
      `;
      container.style.display = 'flex';
    }

    _updateATCButton() {
      const btn = this.querySelector('[data-rs-calc-action="add-to-cart"]');
      if (!btn) return;

      const variant = this._getSelectedVariant();
      if (variant && this.boxesNeeded > 0) {
        const total = (variant.price / 100) * this.boxesNeeded;
        const priceText = this._formatMoney(total);
        btn.textContent = `Add to Cart — ${priceText}`;
        btn.disabled = false;
        this._syncStickyPrice(priceText);
        this._syncPdpPrice(priceText, total);
      } else {
        btn.textContent = 'Add to Cart';
        btn.disabled = true;
        this._restorePdpPrice();
      }
    }

    /** Keep sticky bar price in sync with the calculator ATC total. */
    _syncStickyPrice(priceText) {
      document.querySelectorAll('#sticky-container .sticky-container__atc-price').forEach((el) => {
        el.setAttribute('data-calc-sticky-locked', 'true');
        el.textContent = priceText;
      });
    }

    _pdpPriceRoot() {
      return document.getElementById('pdp-price-container') || document.querySelector('.product-info__price');
    }

    _pdpPriceNodes(root) {
      return [...root.querySelectorAll('.ht-money, .price__current .js-value, .price__was .js-value, .price__default .js-value')]
        .filter((n) => !n.closest('.rs-pps'));
    }

    _parseMoney(text) {
      const raw = String(text || '').replace(/[^0-9.,]/g, '').replace(/,/g, '');
      const n = parseFloat(raw);
      return Number.isFinite(n) ? n : null;
    }

    _ensurePdpPriceSnapshot(root) {
      const existing = root.__calcPriceNodes;
      if (existing && existing[0] && !document.contains(existing[0])) {
        delete root.dataset.calcPriceSnapshot;
        root.__calcPriceNodes = null;
      }
      if (root.dataset.calcPriceSnapshot && root.__calcPriceNodes) return;

      const nodes = this._pdpPriceNodes(root);
      root.__calcPriceNodes = nodes;
      root.dataset.calcPriceSnapshot = JSON.stringify(nodes.map((n) => n.textContent));
    }

    /** Mirror calculator ATC total onto the main PDP price; leave per-SQFT alone. */
    _syncPdpPrice(priceText, totalAmount) {
      const root = this._pdpPriceRoot();
      if (!root || !totalAmount || totalAmount <= 0) {
        this._restorePdpPrice(root);
        return;
      }

      this._ensurePdpPriceSnapshot(root);
      root.setAttribute('data-calc-price-locked', 'true');

      const nodes = root.__calcPriceNodes || [];
      const originals = JSON.parse(root.dataset.calcPriceSnapshot || '[]');

      let primaryOrig = null;
      nodes.forEach((node, i) => {
        if (node.closest('.price--strike, .price__was')) return;
        const n = this._parseMoney(originals[i]);
        if (n != null && n > 0 && primaryOrig == null) primaryOrig = n;
      });
      const ratio = primaryOrig > 0 ? totalAmount / primaryOrig : null;

      nodes.forEach((node, i) => {
        if (node.closest('.price--strike, .price__was')) {
          const unit = this._parseMoney(originals[i]);
          if (unit != null && ratio != null) node.textContent = this._formatMoney(unit * ratio);
          return;
        }
        node.textContent = priceText;
      });
    }

    _restorePdpPrice(root) {
      const el = root || this._pdpPriceRoot();
      if (!el) return;

      const nodes = el.__calcPriceNodes || [];
      const originals = el.dataset.calcPriceSnapshot ? JSON.parse(el.dataset.calcPriceSnapshot) : null;
      if (originals) {
        nodes.forEach((node, i) => {
          if (originals[i] != null) node.textContent = originals[i];
        });
      }

      el.removeAttribute('data-calc-price-locked');
      delete el.dataset.calcPriceSnapshot;
      el.__calcPriceNodes = null;
    }

    /* ── Cart Integration ─────────────────────────────────────────────── */

    async _addToCart() {
      const variant = this._getSelectedVariant();
      if (!variant || this.boxesNeeded <= 0) return;

      let sections = 'cart-icon-bubble';
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        const sectionEl = cartDrawer.closest('.shopify-section');
        if (sectionEl?.id) {
          sections += `,${sectionEl.id.replace('shopify-section-', '')}`;
        }
      }

      const addUrl =
        window.Shopify?.routes?.root != null
          ? `${window.Shopify.routes.root}cart/add.js`
          : '/cart/add.js';

      const atcBtn = this.querySelector('[data-rs-calc-action="add-to-cart"]');
      if (atcBtn) {
        atcBtn.disabled = true;
        atcBtn.textContent = 'Adding...';
      }

      try {
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: variant.id, quantity: this.boxesNeeded }],
            sections,
            sections_url: `${window.location.pathname || '/'}?drawer_pass=core`
          })
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          const msg =
            typeof data.description === 'string'
              ? data.description
              : data.message || 'Cart add failed';
          throw new Error(msg);
        }

        const openDrawer =
          typeof theme !== 'undefined' &&
          theme.settings &&
          theme.settings.afterAtc === 'drawer';

        if (data.sections && cartDrawer) {
          await cartDrawer.hydrateAfterAdd(data, { openDrawer });
        } else if (data.sections) {
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble && data.sections['cart-icon-bubble']) {
            bubble.innerHTML = data.sections['cart-icon-bubble'];
          }
        } else if (cartDrawer?.refresh) {
          await cartDrawer.refresh(false);
          if (openDrawer) {
            document.dispatchEvent(
              new CustomEvent('dispatch:cart-drawer:open', {
                bubbles: true,
                detail: { opener: document.getElementById('cart-icon') }
              })
            );
          }
        }

        document.querySelector('product-form')?.dispatchEvent(
          new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: { cart: data, variantId: variant.id }
          })
        );

        this._showConfirmation();
        this._updateCartCount();
      } catch (err) {
        console.error('RS_FloorCalculator: Cart error', err);
        this._showError('Failed to add to cart. Please try again.');
      } finally {
        if (atcBtn) {
          atcBtn.disabled = false;
          this._updateATCButton();
        }
      }
    }

    _updateCartCount() {
      fetch('/cart.js')
        .then((r) => r.json())
        .then((cart) => {
          document.querySelectorAll('.cart-count, [data-cart-count]').forEach((el) => {
            el.textContent = cart.item_count;
          });
        })
        .catch(() => {});
    }

    /* ── Confirmation & Error ─────────────────────────────────────────── */

    _showConfirmation() {
      const confirm = this.querySelector('[data-rs-calc-confirm]');
      if (!confirm) return;
      const textEl = confirm.querySelector('.rs-floor-calc__confirm-text');
      const subEl = confirm.querySelector('.rs-floor-calc__confirm-sub');
      if (textEl) textEl.textContent = 'Added to Cart!';
      if (subEl) {
        subEl.textContent = `${this.boxesNeeded} box${this.boxesNeeded === 1 ? '' : 'es'} added successfully`;
      }
      confirm.classList.add('rs-floor-calc__confirm--visible');
    }

    _showError(message) {
      const errorEl = this.querySelector('[data-rs-calc-error]');
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.classList.add('rs-floor-calc__error--visible');
      setTimeout(() => errorEl.classList.remove('rs-floor-calc__error--visible'), 5000);
    }

    /* ── Utilities ────────────────────────────────────────────────────── */

    _formatMoney(amount) {
      return '$' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
  }

  customElements.define('rs-floor-calculator', RS_FloorCalculator);
}
